/**
 * URL codec for the listing filters that need more than a flat comma list.
 *
 * Lives apart from `listService` on purpose: the service pulls in the
 * server-only Supabase client, so a client component importing a *value*
 * from it would drag that into the browser bundle. Type-only imports are
 * erased and stay fine; these functions are not. `ProductFilters` and
 * `ActiveFilterChips` import from here, and so does the service — one
 * definition, so the parser and the UI can never drift apart.
 *
 * Variant options travel as repeated `opt` params carrying `group:value`:
 *
 *   ?opt=الحجم:L&opt=الحجم:M&opt=الشكل:مربع
 *
 * The group is `option_groups.name_ar` and the value is
 * `option_values.value_ar` — the exact pair stored in
 * `product_variants.options`, so a round-trip needs no lookup table.
 * `option_groups` has no slug column, and Arabic slugs are already the norm
 * in this codebase (categories and tags both use them).
 */

import type {
  ListingScope,
  ListingSort,
} from "./listService";

/** Every sort the listing understands. Order is irrelevant; membership is not. */
export const LISTING_SORTS: readonly ListingSort[] = [
  "newest",
  "price_asc",
  "price_desc",
  "random",
];

/**
 * The sort a listing uses when the URL carries none.
 *
 * All-products is a browse surface, so it shuffles. Search deliberately does
 * not — someone who typed a query wants relevance, not a reshuffle — and the
 * scoped listings stay newest-first.
 *
 * Lives here rather than in listService because SortDropdown and
 * ProductListLayout are client components: importing it as a value from the
 * service would pull next/headers into the browser bundle.
 */
export function defaultSortForScope(scope: ListingScope): ListingSort {
  // A query typed on all-products is a search, so it gets search's order.
  return scope.type === "all" && !scope.query?.trim() ? "random" : "newest";
}

/** Repeated query param carrying one `group:value` pair each. */
export const VARIANT_OPTION_PARAM = "opt";

/** group (name_ar) → selected values (value_ar). */
export type VariantOptionSelection = Record<string, string[]>;

/**
 * Split on the FIRST colon only: option values may legitimately contain one
 * (e.g. a size written "2:1"), group names in practice do not.
 */
function splitPair(raw: string): [string, string] | null {
  const idx = raw.indexOf(":");
  if (idx <= 0) return null;
  const group = raw.slice(0, idx).trim();
  const value = raw.slice(idx + 1).trim();
  if (!group || !value) return null;
  return [group, value];
}

/**
 * Parse `opt` into a selection map. Accepts a repeated param, a
 * comma-separated one, or any mix — matching how the other list filters
 * tolerate both forms. Unparseable entries are dropped rather than throwing:
 * a hand-edited URL should degrade to fewer filters, not a 500.
 */
export function parseVariantOptions(
  raw: string | string[] | undefined,
): VariantOptionSelection {
  if (!raw) return {};
  const parts = (Array.isArray(raw) ? raw : [raw]).flatMap((s) =>
    s.split(",").filter(Boolean),
  );

  const out: VariantOptionSelection = {};
  for (const part of parts) {
    const pair = splitPair(part);
    if (!pair) continue;
    const [group, value] = pair;
    const values = out[group] ?? [];
    if (!values.includes(value)) values.push(value);
    out[group] = values;
  }
  return out;
}

/** Flatten a selection back to `group:value` strings, for URL writing. */
export function variantOptionPairs(
  selection: VariantOptionSelection,
): string[] {
  const out: string[] = [];
  for (const [group, values] of Object.entries(selection)) {
    for (const value of values) out.push(`${group}:${value}`);
  }
  return out;
}

/** Rewrite the `opt` params on `params` to match `selection`. */
export function writeVariantOptions(
  params: URLSearchParams,
  selection: VariantOptionSelection,
): void {
  params.delete(VARIANT_OPTION_PARAM);
  for (const pair of variantOptionPairs(selection)) {
    params.append(VARIANT_OPTION_PARAM, pair);
  }
}

/** Add or remove one value, returning a new selection (never mutates). */
export function toggleVariantOption(
  selection: VariantOptionSelection,
  group: string,
  value: string,
): VariantOptionSelection {
  const current = selection[group] ?? [];
  const next = current.includes(value)
    ? current.filter((v) => v !== value)
    : [...current, value];

  const out: VariantOptionSelection = { ...selection };
  if (next.length === 0) delete out[group];
  else out[group] = next;
  return out;
}

/** True when at least one group has a selected value. */
export function hasVariantOptions(selection: VariantOptionSelection): boolean {
  return Object.values(selection).some((v) => v.length > 0);
}

/**
 * Intersect id lists, treating `null` as "unrestricted". Used to fold each
 * filter's product-id allow-list into one set. Order follows the first
 * non-null list so callers get a stable result.
 */
export function intersectIdLists(
  lists: Array<string[] | null | undefined>,
): string[] | null {
  let acc: string[] | null = null;
  for (const list of lists) {
    if (list === null || list === undefined) continue;
    if (acc === null) {
      acc = [...list];
      continue;
    }
    const set = new Set(list);
    acc = acc.filter((id) => set.has(id));
  }
  return acc;
}
